import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Theme = 'slate' | 'studio' | 'midnight';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  themeClasses: {
    bg: string;
    text: string;
    textMuted: string;
    textSoft: string;
    card: string;
    border: string;
    accent: string;
    gradient: string;
  };
  /** Common accent utility classes for the active theme */
  accent: {
    text: string;
    bg: string;
    border: string;
    hoverText: string;
    hoverBg: string;
    softBg: string;
    softBorder: string;
    button: string;
    buttonOutline: string;
  };
  isLight: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const themeConfig: Record<Theme, ThemeContextType['themeClasses']> = {
  slate: {
    bg: 'bg-[#0b0f14]',
    text: 'text-slate-100',
    textMuted: 'text-slate-300',
    textSoft: 'text-slate-400',
    card: 'bg-[#12171f]/90 backdrop-blur-sm border border-white/10',
    border: 'border-teal-400',
    accent: 'teal-400',
    gradient: 'from-teal-400 via-sky-500 to-teal-500'
  },
  studio: {
    bg: 'bg-zinc-50',
    text: 'text-zinc-900',
    textMuted: 'text-zinc-600',
    textSoft: 'text-zinc-500',
    card: 'bg-white border border-zinc-200 shadow-sm',
    border: 'border-teal-700',
    accent: 'teal-700',
    gradient: 'from-teal-700 via-sky-700 to-zinc-800'
  },
  midnight: {
    bg: 'bg-slate-950',
    text: 'text-sky-50',
    textMuted: 'text-sky-100/80',
    textSoft: 'text-sky-200/60',
    card: 'bg-slate-900/90 backdrop-blur-sm border border-sky-500/20',
    border: 'border-sky-400',
    accent: 'sky-400',
    gradient: 'from-sky-400 via-blue-500 to-cyan-400'
  }
};

const accentConfig: Record<Theme, ThemeContextType['accent']> = {
  slate: {
    text: 'text-teal-400',
    bg: 'bg-teal-400',
    border: 'border-teal-400',
    hoverText: 'hover:text-teal-300',
    hoverBg: 'hover:bg-teal-400',
    softBg: 'bg-teal-400/10',
    softBorder: 'border-teal-400/30',
    button: 'bg-teal-400 text-slate-950 hover:bg-teal-300',
    buttonOutline: 'border-teal-400 text-teal-400 hover:bg-teal-400/10'
  },
  studio: {
    text: 'text-teal-700',
    bg: 'bg-teal-700',
    border: 'border-teal-700',
    hoverText: 'hover:text-teal-800',
    hoverBg: 'hover:bg-teal-700',
    softBg: 'bg-teal-700/10',
    softBorder: 'border-teal-700/30',
    button: 'bg-teal-700 text-white hover:bg-teal-800',
    buttonOutline: 'border-teal-700 text-teal-700 hover:bg-teal-700/10'
  },
  midnight: {
    text: 'text-sky-400',
    bg: 'bg-sky-400',
    border: 'border-sky-400',
    hoverText: 'hover:text-sky-300',
    hoverBg: 'hover:bg-sky-400',
    softBg: 'bg-sky-400/10',
    softBorder: 'border-sky-400/30',
    button: 'bg-sky-400 text-slate-950 hover:bg-sky-300',
    buttonOutline: 'border-sky-400 text-sky-400 hover:bg-sky-400/10'
  }
};

const LEGACY_THEME_MAP: Record<string, Theme> = {
  terminal: 'slate',
  matrix: 'slate',
  neon: 'slate',
  ocean: 'slate',
  cyberpunk: 'midnight',
  minimal: 'studio',
  slate: 'slate',
  studio: 'studio',
  midnight: 'midnight'
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('theme');
        if (saved && LEGACY_THEME_MAP[saved]) {
          return LEGACY_THEME_MAP[saved];
        }
      }
    } catch (error) {
      console.warn('Failed to read theme from localStorage:', error);
    }
    return 'slate';
  });

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', newTheme);
        document.documentElement.setAttribute('data-theme', newTheme);
      }
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme);
      // Persist migrated theme name
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  const themeClasses = themeConfig[theme] || themeConfig.slate;
  const accent = accentConfig[theme] || accentConfig.slate;

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        themeClasses,
        accent,
        isLight: theme === 'studio'
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const getThemeClasses = (theme: Theme) => {
  const config = themeConfig[theme];
  return {
    ...config,
    accent: accentConfig[theme]
  };
};

/** Heading text that stays readable on light and dark themes */
export const headingText = (isLight: boolean) =>
  isLight ? 'text-zinc-900' : 'text-white';
